/**
 * app-shell - Main application shell component
 * Contains header, router outlet, and toast notifications
 * Handles navigation events and coordinates with auth state
 */
import { BaseElement } from '../base-element';
import {
  authActions,
  authState,
  canAccessAppAdminConfiguration,
  canAccessSecretaryFeatures,
  canManageTrialEvents,
} from '../../state/auth.state';
import './app-header';
import './app-toast';
import '../pages/trials-page';
import '../pages/login-page';
import '../pages/auth-callback-page';
import '../pages/confirmation-page';
import '../pages/error-page';
import '../pages/forbidden-page';
import '../pages/registration/registration-page';
import '../pages/admin/form-templates-page';
import '../pages/admin/organization-sport-codes-page';
import '../pages/admin/organizations-page';
import '../pages/admin/sport-codes-page';
import '../pages/secretary/secretary-list-page';
import '../pages/secretary/secretary-detail-page';
import '../pages/secretary/trial-event-detail-page';
import { getAuthCallbackPath, getCurrentAppPath, setReturnUrl } from '../../auth-navigation';
import { navigationAdapter } from '../../router';

export class AppShell extends BaseElement {
  private currentPath: string = '/';
  private readonly popStateHandler = () => this.syncWithLocation();
  private readonly pageShowHandler = () => this.syncWithLocation();

  connectedCallback(): void {
    this.currentPath = window.location.pathname || '/';

    this.watch(authState, () => {
      this.updateRender();
    });

    this.updateRender();
    this.setupEventListeners();

    window.addEventListener('popstate', this.popStateHandler);
    window.addEventListener('pageshow', this.pageShowHandler);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.popStateHandler);
    window.removeEventListener('pageshow', this.pageShowHandler);
  }

  private setupEventListeners(): void {
    this.addEventListener('navigate', ((e: CustomEvent<{ path: string }>) => {
      this.navigateTo(e.detail.path);
    }) as EventListener);

    this.addEventListener('login-click', () => {
      const scheduleRedirect =
        typeof window.requestAnimationFrame === 'function'
          ? window.requestAnimationFrame.bind(window)
          : (callback: FrameRequestCallback) => window.setTimeout(() => callback(0), 0);

      scheduleRedirect(() => {
        void authActions.login();
      });
    });

    this.addEventListener('logout-click', () => {
      void authActions.logout();
      this.navigateTo('/');
    });
  }

  private syncWithLocation(): void {
    this.currentPath = window.location.pathname || '/';
    this.updateRender();
    this.emit('route-changed', { path: this.currentPath });
  }

  public navigateTo(path: string): void {
    if (this.currentPath !== path) {
      this.currentPath = path;
      window.history.pushState({}, '', path);
      this.updateRender();
      this.emit('route-changed', { path: this.currentPath });
    }
  }

  public getCurrentPath(): string {
    return this.currentPath;
  }

  private replacePath(path: string): void {
    const browserPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (browserPath !== path) {
      window.history.replaceState({}, '', path);
    }

    if (this.currentPath !== path) {
      this.currentPath = path;
      this.emit('route-changed', { path: this.currentPath });
    }
  }

  private redirectToLogin(): string {
    setReturnUrl(getCurrentAppPath());
    this.replacePath('/login');

    return this.renderLoginContent();
  }

  private redirectToForbidden(): string {
    this.replacePath('/forbidden');

    return `<dt-forbidden-page data-testid="forbidden-page-wrapper"></dt-forbidden-page>`;
  }

  private ensureAuthenticated(): string | null {
    if (!authState.get().isAuthenticated) {
      return this.redirectToLogin();
    }

    return null;
  }

  private ensureSecretaryAccess(): string | null {
    const loginRedirect = this.ensureAuthenticated();
    if (loginRedirect) {
      return loginRedirect;
    }

    if (!canAccessSecretaryFeatures.get()) {
      return this.redirectToForbidden();
    }

    return null;
  }

  private ensureTrialEventManagementAccess(): string | null {
    const loginRedirect = this.ensureAuthenticated();
    if (loginRedirect) {
      return loginRedirect;
    }

    if (!canManageTrialEvents.get()) {
      return this.redirectToForbidden();
    }

    return null;
  }

  private ensureAppAdminConfigurationAccess(): string | null {
    const loginRedirect = this.ensureAuthenticated();
    if (loginRedirect) {
      return loginRedirect;
    }

    if (!canAccessAppAdminConfiguration.get()) {
      return this.redirectToForbidden();
    }

    return null;
  }

  private renderLoginContent(): string {
    if (authState.get().isLoading) {
      return `
        <div class="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center" data-testid="login-interstitial">
          <dt-spinner size="large"></dt-spinner>
          <div class="space-y-2">
            <h1 class="text-2xl font-semibold text-neutral-900 dark:text-white">Just a second while we get you logged in...</h1>
            <p class="max-w-md text-sm text-neutral-500 dark:text-neutral-400">You can use your browser Back button again if you want to stay in the app instead.</p>
          </div>
        </div>
      `;
    }

    return `<dt-login-page data-testid="login-page"></dt-login-page>`;
  }

  private renderAuthHandoffOverlay(): string {
    if (!authState.get().isLoading || this.currentPath === '/login') {
      return '';
    }

    return `
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/55 px-4 backdrop-blur-sm" data-testid="auth-handoff-overlay">
        <div class="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl dark:bg-surface-dark">
          <div class="flex flex-col items-center gap-4">
            <dt-spinner size="large"></dt-spinner>
            <div class="space-y-2">
              <h2 class="text-2xl font-semibold text-neutral-900 dark:text-white">Just a second while we get you logged in...</h2>
              <p class="text-sm text-neutral-500 dark:text-neutral-400">We’re opening the secure sign-in flow and will bring you right back here when it finishes.</p>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderDesignSystemRedirect(): string {
    navigationAdapter.redirectToDesignSystem();

    return `
      <div class="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-6 text-center" data-testid="design-system-redirect">
        <dt-spinner size="large"></dt-spinner>
        <div class="space-y-2">
          <h1 class="text-2xl font-semibold text-neutral-900 dark:text-white">Opening the design system...</h1>
          <p class="max-w-md text-sm text-neutral-500 dark:text-neutral-400">The standalone component showcase is loading in its dedicated entry point.</p>
        </div>
      </div>
    `;
  }

  private renderContent(): string {
    const path = this.currentPath;

    if (path === '/' || path === '/trials') {
      return `<dt-trials-page data-testid="trials-page"></dt-trials-page>`;
    }

    if (path === '/design-system') {
      return this.renderDesignSystemRedirect();
    }

    const confirmationMatch = path.match(/^\/trial-events\/([^/]+)\/confirmation\/([^/]+)$/);
    if (confirmationMatch) {
      const trialEventId = confirmationMatch[1];
      const entryId = confirmationMatch[2];

      return `<dt-confirmation-page trial-event-id="${trialEventId}" entry-id="${entryId}" data-testid="confirmation-page"></dt-confirmation-page>`;
    }

    const registerMatch = path.match(/^\/trials\/([^/]+)\/register$/);
    if (registerMatch) {
      const loginRedirect = this.ensureAuthenticated();
      if (loginRedirect) {
        return loginRedirect;
      }

      const trialEventId = registerMatch[1];
      return `<dt-registration-page trial-event-id="${trialEventId}" data-testid="registration-page"></dt-registration-page>`;
    }

    const secretaryDetailMatch = path.match(/^\/secretary\/entries\/([^/]+)\/([^/]+)$/);
    if (secretaryDetailMatch) {
      const deniedContent = this.ensureSecretaryAccess();
      if (deniedContent) {
        return deniedContent;
      }

      const trialEventId = secretaryDetailMatch[1];
      const entryId = secretaryDetailMatch[2];
      return `<dt-secretary-detail-page trial-event-id="${trialEventId}" entry-id="${entryId}" data-testid="secretary-detail-page-wrapper"></dt-secretary-detail-page>`;
    }

    if (path === '/secretary/trial-events/new') {
      const deniedContent = this.ensureTrialEventManagementAccess();
      if (deniedContent) return deniedContent;
      return '<dt-trial-event-detail-page create></dt-trial-event-detail-page>';
    }

    const trialEventDetailMatch = path.match(/^\/admin\/trial-events\/([^/]+)$/);
    if (trialEventDetailMatch) {
      const deniedContent = this.ensureTrialEventManagementAccess();
      if (deniedContent) {
        return deniedContent;
      }

      const trialEventId = trialEventDetailMatch[1];
      return `<dt-trial-event-detail-page trial-event-id="${trialEventId}" data-testid="trial-event-detail-page-wrapper"></dt-trial-event-detail-page>`;
    }

    const organizationSportCodesMatch = path.match(/^\/admin\/configuration\/organizations\/([^/]+)\/sport-codes$/);
    if (organizationSportCodesMatch) {
      const deniedContent = this.ensureAppAdminConfigurationAccess();
      if (deniedContent) {
        return deniedContent;
      }

      const organizationId = organizationSportCodesMatch[1];
      return `<dt-admin-organization-sport-codes-page organization-id="${organizationId}" data-testid="admin-organization-sport-codes-page-wrapper"></dt-admin-organization-sport-codes-page>`;
    }

    if (
      path === '/admin/configuration' ||
      path === '/admin/configuration/sport-codes' ||
      path === '/admin/configuration/organizations' ||
      path === '/admin/configuration/form-templates'
    ) {
      const deniedContent = this.ensureAppAdminConfigurationAccess();
      if (deniedContent) {
        return deniedContent;
      }

      if (path === '/admin/configuration') {
        this.replacePath('/admin/configuration/organizations');
      }

      if (this.currentPath === '/admin/configuration/sport-codes') {
        return '<dt-admin-sport-codes-page data-testid="admin-sport-codes-page-wrapper"></dt-admin-sport-codes-page>';
      }

      if (this.currentPath === '/admin/configuration/form-templates') {
        return '<dt-admin-form-templates-page data-testid="admin-form-templates-page-wrapper"></dt-admin-form-templates-page>';
      }

      return '<dt-admin-organizations-page data-testid="admin-organizations-page-wrapper"></dt-admin-organizations-page>';
    }

    if (path === '/secretary') {
      const deniedContent = this.ensureTrialEventManagementAccess();
      if (deniedContent) {
        return deniedContent;
      }

      return `<dt-secretary-page data-testid="secretary-page-wrapper"></dt-secretary-page>`;
    }

    if (path === '/login') {
      return this.renderLoginContent();
    }

    if (path === getAuthCallbackPath()) {
      return `<dt-auth-callback-page data-testid="auth-callback-page"></dt-auth-callback-page>`;
    }

    if (path === '/forbidden') {
      return `<dt-forbidden-page data-testid="forbidden-page-wrapper"></dt-forbidden-page>`;
    }

    if (path === '/error') {
      return `<dt-error-page data-testid="error-page-wrapper"></dt-error-page>`;
    }

    const routes: Record<string, { title: string; icon: string }> = {
      '/register': { title: 'Register', icon: '📝' },
      '/my-entries': { title: 'My Entries', icon: '📋' },
      '/profile': { title: 'Profile', icon: '👤' },
    };

    let matchedRoute = routes[path];

    if (!matchedRoute && path.startsWith('/register/')) {
      matchedRoute = routes['/register'];
    }

    if (!matchedRoute) {
      return `<dt-error-page code="404" data-testid="error-page-404"></dt-error-page>`;
    }

    return `
      <div class="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center" data-route="${path}">
        <div class="mb-4 text-5xl">${matchedRoute.icon}</div>
        <h1 class="mb-2 text-2xl font-bold text-neutral-900 dark:text-white">${matchedRoute.title}</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400">This page will be implemented in a future work item.</p>
      </div>
    `;
  }

  private updateRender(): void {
    this.className = 'bg-background-light dark:bg-background-dark';

    this.render(`
      <div class="flex min-h-screen flex-col">
        <app-header></app-header>

        <main class="flex-1 pt-0">
          <div class="mx-auto w-full max-w-7xl p-4 md:p-6 lg:p-8">
            <div class="min-h-[calc(100vh-200px)]" data-testid="router-outlet">
              ${this.renderContent()}
            </div>
          </div>
        </main>
      </div>

      ${this.renderAuthHandoffOverlay()}

      <app-toast></app-toast>
    `);
  }
}

customElements.define('app-shell', AppShell);
